<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { MASKING_PRESETS, maskText } from 'shared/helpers/dataMasking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const store = useStore();
const { t } = useI18n();

const PRESET_IDS = Object.keys(MASKING_PRESETS);

// Metadados visuais por preset (ícone + exemplo ilustrativo antes→depois).
const PRESET_META = {
  credit_card: {
    icon: 'i-lucide-credit-card',
    example: '4111 2222 3333 4444 → •••• •••• •••• 4444',
  },
  cpf: {
    icon: 'i-lucide-user-round',
    example: '123.456.789-09 → ***.***.***-**',
  },
  cnpj: {
    icon: 'i-lucide-building-2',
    example: '12.345.678/0001-95 → **.***.***/****-**',
  },
  pin_4: { icon: 'i-lucide-key-round', example: '1234 → ••••' },
};

const enabled = ref(false);
const applyToOutgoing = ref(true);
const enabledPresets = ref({}); // { credit_card: true, ... }
const customRules = ref([]); // [{ pattern, flags, replacement, label, enabled }]
const testInput = ref('');

const hydrate = config => {
  enabled.value = config?.enabled === true;
  applyToOutgoing.value = config?.applyToOutgoing !== false;
  const presetState = {};
  PRESET_IDS.forEach(id => {
    presetState[id] = false;
  });
  customRules.value = [];
  (config?.rules || []).forEach(rule => {
    if (rule.type === 'preset' && presetState[rule.preset] !== undefined) {
      presetState[rule.preset] = rule.enabled !== false;
    } else if (rule.type === 'regex') {
      customRules.value.push({
        pattern: rule.pattern || '',
        flags: rule.flags || 'g',
        replacement: rule.replacement || '',
        label: rule.label || '',
        enabled: rule.enabled !== false,
      });
    }
  });
  enabledPresets.value = presetState;
};

watch(() => props.inbox?.maskingConfig, hydrate, {
  immediate: true,
  deep: true,
});

const presetCount = computed(
  () => Object.values(enabledPresets.value).filter(Boolean).length
);

const togglePreset = id => {
  enabledPresets.value[id] = !enabledPresets.value[id];
};

const addCustomRule = () => {
  customRules.value.push({
    pattern: '',
    flags: 'g',
    replacement: '',
    label: '',
    enabled: true,
  });
};

const removeCustomRule = index => {
  customRules.value.splice(index, 1);
};

const buildRules = () => {
  const rules = [];
  PRESET_IDS.forEach(id => {
    rules.push({
      type: 'preset',
      preset: id,
      enabled: !!enabledPresets.value[id],
    });
  });
  customRules.value
    .filter(r => r.pattern.trim())
    .forEach(r =>
      rules.push({
        type: 'regex',
        pattern: r.pattern.trim(),
        flags: r.flags || 'g',
        replacement: r.replacement,
        label: r.label,
        enabled: r.enabled !== false,
      })
    );
  return rules;
};

const testOutput = computed(() =>
  testInput.value ? maskText(testInput.value, buildRules()) : ''
);

const testChanged = computed(
  () => testInput.value && testOutput.value !== testInput.value
);

const save = async () => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      masking_config: {
        enabled: enabled.value,
        apply_to_outgoing: applyToOutgoing.value,
        rules: buildRules(),
      },
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 mx-6 max-w-4xl">
    <SettingsToggleSection
      v-model="enabled"
      :header="$t('INBOX_MGMT.MASKING.ENABLE_LABEL')"
      :description="$t('INBOX_MGMT.MASKING.DESCRIPTION')"
    />

    <!-- Aviso de segurança como banner informativo -->
    <div
      class="flex gap-2 items-start p-3 rounded-lg bg-n-amber-2 text-n-amber-12"
    >
      <span class="mt-0.5 i-lucide-shield-alert size-4 shrink-0 text-n-amber-11" />
      <p class="text-body-small leading-relaxed">
        {{ $t('INBOX_MGMT.MASKING.SECURITY_NOTE') }}
      </p>
    </div>

    <template v-if="enabled">
      <SettingsToggleSection
        v-model="applyToOutgoing"
        :header="$t('INBOX_MGMT.MASKING.APPLY_TO_OUTGOING_LABEL')"
        :description="$t('INBOX_MGMT.MASKING.APPLY_TO_OUTGOING_HELP')"
      />

      <!-- Predefinições como cards selecionáveis -->
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-0.5">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.MASKING.TITLE') }}
          </span>
          <span class="text-body-small text-n-slate-11">
            {{ $t('INBOX_MGMT.MASKING.PRESETS_HELP') }}
          </span>
        </div>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            v-for="id in PRESET_IDS"
            :key="id"
            type="button"
            class="flex gap-3 items-start p-3 text-left rounded-xl border transition-all"
            :class="
              enabledPresets[id]
                ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand/30'
                : 'border-n-weak hover:border-n-slate-6 hover:bg-n-alpha-1'
            "
            @click="togglePreset(id)"
          >
            <span
              class="flex justify-center items-center rounded-lg size-9 shrink-0"
              :class="
                enabledPresets[id]
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              <span :class="[PRESET_META[id].icon, 'size-4.5']" />
            </span>
            <span class="flex flex-col flex-1 gap-0.5 min-w-0">
              <span class="flex gap-2 justify-between items-center">
                <span class="text-body-main font-medium text-n-slate-12">
                  {{ $t(`INBOX_MGMT.MASKING.PRESETS.${id.toUpperCase()}`) }}
                </span>
                <span
                  class="size-4 shrink-0"
                  :class="
                    enabledPresets[id]
                      ? 'i-lucide-check-circle-2 text-n-brand'
                      : 'i-lucide-circle text-n-slate-6'
                  "
                />
              </span>
              <span class="text-body-small text-n-slate-11">
                {{ $t(`INBOX_MGMT.MASKING.PRESETS.${id.toUpperCase()}_DESC`) }}
              </span>
              <span
                class="mt-1 font-mono text-label-small text-n-slate-10 truncate"
              >
                {{ PRESET_META[id].example }}
              </span>
              <span
                v-if="id === 'pin_4'"
                class="mt-1 text-label-small text-n-amber-11"
              >
                {{ $t('INBOX_MGMT.MASKING.PRESETS.PIN_4_WARNING') }}
              </span>
            </span>
          </button>
        </div>
      </div>

      <!-- Regras personalizadas -->
      <div class="flex flex-col gap-3">
        <div class="flex justify-between items-center">
          <div class="flex flex-col gap-0.5">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.MASKING.CUSTOM.TITLE') }}
            </span>
            <span class="text-body-small text-n-slate-11">
              {{ $t('INBOX_MGMT.MASKING.CUSTOM.HELP') }}
            </span>
          </div>
          <NextButton
            sm
            faded
            slate
            icon="i-lucide-plus"
            :label="$t('INBOX_MGMT.MASKING.CUSTOM.ADD_RULE')"
            @click="addCustomRule"
          />
        </div>

        <!-- Estado vazio -->
        <div
          v-if="!customRules.length"
          class="flex flex-col gap-1 items-center px-4 py-8 text-center rounded-xl border border-dashed border-n-weak"
        >
          <span class="mb-1 i-lucide-regex size-6 text-n-slate-9" />
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.MASKING.CUSTOM.EMPTY_TITLE') }}
          </span>
          <span class="max-w-sm text-body-small text-n-slate-10">
            {{ $t('INBOX_MGMT.MASKING.CUSTOM.EMPTY_DESC') }}
          </span>
        </div>

        <div
          v-for="(rule, index) in customRules"
          :key="index"
          class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak"
        >
          <div class="flex gap-2 items-center">
            <input
              v-model="rule.label"
              type="text"
              class="flex-1 !mb-0 h-9 text-sm"
              :placeholder="$t('INBOX_MGMT.MASKING.CUSTOM.LABEL')"
            />
            <NextButton
              sm
              ghost
              ruby
              icon="i-lucide-trash-2"
              @click="removeCustomRule(index)"
            />
          </div>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <input
              v-model="rule.pattern"
              type="text"
              class="!mb-0 h-9 font-mono text-sm sm:col-span-2"
              :placeholder="$t('INBOX_MGMT.MASKING.CUSTOM.PATTERN')"
            />
            <input
              v-model="rule.flags"
              type="text"
              class="!mb-0 h-9 font-mono text-sm"
              :placeholder="$t('INBOX_MGMT.MASKING.CUSTOM.FLAGS')"
            />
          </div>
          <input
            v-model="rule.replacement"
            type="text"
            class="!mb-0 h-9 font-mono text-sm"
            :placeholder="$t('INBOX_MGMT.MASKING.CUSTOM.REPLACEMENT')"
          />
        </div>
      </div>

      <!-- Testar regras com preview antes→depois -->
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.MASKING.CUSTOM.TEST') }}
          </span>
          <span class="text-body-small text-n-slate-11">
            {{ $t('INBOX_MGMT.MASKING.CUSTOM.TEST_HELP') }}
          </span>
        </div>
        <input
          v-model="testInput"
          type="text"
          class="!mb-0 h-10 text-sm"
          :placeholder="$t('INBOX_MGMT.MASKING.CUSTOM.TEST_PLACEHOLDER')"
        />
        <div
          class="flex flex-col gap-2 p-3 rounded-xl border sm:flex-row sm:items-center border-n-weak bg-n-alpha-1"
        >
          <div class="flex flex-col gap-0.5 flex-1 min-w-0">
            <span class="uppercase text-label-small text-n-slate-10">
              {{ $t('INBOX_MGMT.MASKING.CUSTOM.TEST_BEFORE') }}
            </span>
            <span class="font-mono text-sm break-words text-n-slate-11">
              {{ testInput || '-' }}
            </span>
          </div>
          <span
            class="i-lucide-arrow-right size-4 text-n-slate-9 shrink-0 hidden sm:block"
          />
          <div class="flex flex-col gap-0.5 flex-1 min-w-0">
            <span class="uppercase text-label-small text-n-slate-10">
              {{ $t('INBOX_MGMT.MASKING.CUSTOM.TEST_AFTER') }}
            </span>
            <span
              class="font-mono text-sm break-words"
              :class="testChanged ? 'text-n-teal-11 font-medium' : 'text-n-slate-11'"
            >
              {{ testOutput || '-' }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <div class="pt-2">
      <NextButton
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="save"
      />
    </div>
  </div>
</template>
